import { useEffect, useState } from "react";
import { Trophy, Users } from "lucide-react";
import { useLeaderboardStore } from "../../store/leaderboardStore";
import { usePublicStore } from "../../store/publicStore";
import { SeasonLeaderboardTable } from "../../components/leaderboard/seasonLevel/SeasonLeaderboardTable";
import { formatDateToDDMMMYYYY } from "../../utils/dateUtils";
import { Divider } from "../../components/catalyst/divider";
import { KnockoutMatchLeaderboard } from "../../components/leaderboard/KnockoutMatchLeaderboard";

export function SeasonLeaderboard() {
  const [view, setView] = useState<"group" | "team" | "player">("group");
  const {
    seasonPlayerLeaderboard,
    getSeasonPlayerLeaderboard,
    setLeaderboardLevel,
  } = useLeaderboardStore();
  const {
    currentSeason,
    getCurrentSeason,
    matchSetups,
    getMatchSetups,
    getAllGroups,
    allGroups,
  } = usePublicStore();

  useEffect(() => {
    if (!currentSeason) {
      getCurrentSeason();
    }
  }, [getCurrentSeason, currentSeason]);

  useEffect(() => {
    getMatchSetups();
  }, [getMatchSetups]);

  useEffect(() => {
    getAllGroups();
  }, [getAllGroups]);

  useEffect(() => {
    if (currentSeason) {
      getSeasonPlayerLeaderboard(currentSeason.id);
    }
  }, [getSeasonPlayerLeaderboard, currentSeason]);

  return (
    <div className="p-3 space-y-6 transition-colors duration-200 bg-white dark:bg-gray-800 sm:rounded">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Season Leaderboard
        </h1>

        <button
          onClick={() => setLeaderboardLevel("match")}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          View Match level leaderboard
        </button>
      </div>
      {matchSetups
        .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""))
        .filter((m) => m.groupOrKnockout !== "Group")
        .map((match) => (
          <div
            key={match.matchId}
            className="flex flex-col space-y-4 border-t dark:border-gray-700 pt-2"
          >
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {match.courseName} - {formatDateToDDMMMYYYY(match.date ?? "")}
            </h1>
            <KnockoutMatchLeaderboard
              match={match}
              players={seasonPlayerLeaderboard.filter(l => l.matchId === match.matchId)}  
            />
          </div>
        ))}
      <Divider />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            Group Leaderboard
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setView("group")}
            className={`px-4 py-2 rounded-sm flex items-center space-x-2 ${
              view === "group"
                ? "bg-green-600 dark:bg-green-700 text-white"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            <Trophy className="w-5 h-5" />
            <span>Group View</span>
          </button>
          <button
            onClick={() => setView("team")}
            className={`px-4 py-2 rounded-sm flex items-center space-x-2 ${
              view === "team"
                ? "bg-green-600 dark:bg-green-700 text-white"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            <Trophy className="w-5 h-5" />
            <span>Team View</span>
          </button>
          <button
            onClick={() => setView("player")}
            className={`px-4 py-2 rounded-sm flex items-center space-x-2 ${
              view === "player"
                ? "bg-green-600 dark:bg-green-700 text-white"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            <Users className="w-5 h-5" />
            <span>MVP</span>
          </button>
        </div>
      </div>

      <SeasonLeaderboardTable
        data={seasonPlayerLeaderboard}
        view={view}
        groups={allGroups}
      />
    </div>
  );
}
